import { HistoryApi, ConflictOutlineSystem, HighlightAddedEntitySystem, InvalidFramePlacementSystem, SelectionEventSystem, PlannerCoreApi, ModelLoadedEventSystem, UnavailableProductsEventSystem, ConflictSystem, InSceneEventSystem, AnimationSystem, DraggableWhenSelectedSystem, ProductType, HistoryEventSystem, StackableOrganizersValidation, CollisionSystem, HandleTypeValidation, MeasurementsWidgetSystem } from '@inter-ikea-storageone/range';
import { AutomaticGroupBuilderSystem, ComponentIds } from '@inter-ikea-core/core';
import { AllowedDoorPairsValidation, AllowedNumberOfChildrenOfTypeValidation, AlignmentConnectionCreationSystem, FunctionalMarginSystem, FunctionalMarginValidation, PlacementZoneValidation, StandardSelectionSystem, OutlineSystem, RequiredConnectorsSatisfiedValidation, HostConnectorRotationValidation } from '@inter-ikea-core/core';
import { PaxCameraApi } from './PaxCameraApi';
import { PaxEntityApi } from './PaxEntityApi';
import { PaxSceneApi } from './PaxSceneApi';
import { PaxProductApi } from './PaxProductApi';
import { PaxLightsAccessoriesSystem } from './systems/PaxLightsAccessoriesSystem';
import { AutoAdaptAccessoriesSystem } from './systems/AutoAdaptAccessoriesSystem';
import { BoxAndMatsPlacementValidation } from './validations/BoxAndMatsPlacementValidation';
import { InnerLightPlacementValidation } from './validations/InnerLightPlacementValidation';
import { AccessoriesPlacementValidation } from './validations/AccessoriesPlacementValidation';
import { RestrictInteriorsInCornerSolutionsValidation } from './validations/RestrictInteriorsInCornerSolutionsValidation';
import { PaxConflictResolver } from './resolvers/paxConflictResolver';
import { SkubbBoxPlacementValidation } from './validations/SkubbBoxPlacementValidation';
import { DoorsNextToCornerSolutionSystem } from './systems/DoorsNextToCornerSolutionSystem';
import { SimpleOutlineSelectedSystem } from '@inter-ikea-core/core-toolbox';
import { CornerHingeSystem } from './systems/CornerHingeSystem';
import { ValetHangerOnCornerAddonFrameValidationRule } from './validations/ValetHangerOnCornerAddonFrameValidationRule';
import { RestrictPulloutInteriorsInDeepCornerAddonFrameValidation } from './validations/RestrictPulloutInteriorsInDeepCornerAddonFrameValidation';
import { RestrictLDividerChildrenValidationRule } from './validations/RestrictLDividerChildrenValidationRule';
import { RestrictDoorsOnCornerBaseFrameValidationRule } from './validations/RestrictDoorsOnCornerBaseFrameValidationRule';
import { LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule } from './validations/LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule';
import CornerAddonFrameComponent from './components/CornerAddonFrameComponent';
import DontAllowPlacementOnCornerAddonFrameComponent from './components/DontAllowPlacementOnCornerAddonFrameComponent';
import { PaxFrameGroupingSystem } from './systems/PaxFrameGroupingSystem';
import { PaxMeasurementsSystem } from './systems/PaxMeasurementsSystem';
import { CornerSolutionShelfSystem } from './systems/CornerSolutionShelfSystem';
import CornerRegularFrameComponent from './components/CornerRegularFrameComponent';
import DontAllowPlacementOnCornerRegularFrameComponent from './components/DontAllowPlacementOnCornerRegularFrameComponent';
import { AutofillCornerSystem } from './systems/AutofillSystems/AutofillCornerSystem';
import { AutofillSingleWallSystem } from './systems/AutofillSystems/AutofillSingleWallSystem';
import { RestrictHandlesOnCornerSolution } from './validations/RestrictHandlesOnCornerSolution';
import { AutoAdaptClothRailSystem } from './systems/AutoAdaptClothRailSystem';
const spaceEnabled = new URLSearchParams(window.location.search).get('enableSpacePlanning') === 'true';
export class PaxCoreApi extends PlannerCoreApi {
    async bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService, placementService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new OutlineSystem());
        this.registerSystem(new AutomaticGroupBuilderSystem(this.core.services.entityManager));
        this.registerSystem(new SimpleOutlineSelectedSystem());
        this.registerSystem(new StandardSelectionSystem(this.core.services.entityManager, this.core.services.sceneManager));
        this.registerSystem(new FunctionalMarginSystem());
        // Register core validations
        this.registerValidation(new AllowedDoorPairsValidation());
        this.registerValidation(new HostConnectorRotationValidation());
        this.registerValidation(new RequiredConnectorsSatisfiedValidation());
        this.registerValidation(new PlacementZoneValidation());
        this.registerValidation(new AllowedNumberOfChildrenOfTypeValidation(ComponentIds.DOOR_FRONT_COMPONENT_ID, ComponentIds.HANDLE_COMPONENT_ID, 1));
        this.registerValidation(new AllowedNumberOfChildrenOfTypeValidation(CornerRegularFrameComponent.id, DontAllowPlacementOnCornerRegularFrameComponent.id, 0));
        this.registerValidation(new AllowedNumberOfChildrenOfTypeValidation(CornerAddonFrameComponent.id, DontAllowPlacementOnCornerAddonFrameComponent.id, 0));
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        // Setup planner APIs
        this.conflictResolver = new PaxConflictResolver(this);
        this.history = new HistoryApi(this);
        this.products = new PaxProductApi(this.core.services, placementService, this.reportErrorToSentry);
        this.entities = new PaxEntityApi(selectionService, this.core, placementService, this.ipexViewer, this.reportErrorToSentry);
        // Register planner validations
        this.registerValidation(new InnerLightPlacementValidation(this));
        this.registerValidation(new BoxAndMatsPlacementValidation(this));
        this.registerValidation(new AccessoriesPlacementValidation(this));
        this.registerValidation(new RestrictInteriorsInCornerSolutionsValidation(this));
        this.registerValidation(new SkubbBoxPlacementValidation(this));
        this.registerValidation(new StackableOrganizersValidation(this));
        this.registerValidation(new HandleTypeValidation(this));
        this.registerValidation(new ValetHangerOnCornerAddonFrameValidationRule(this));
        this.registerValidation(new RestrictPulloutInteriorsInDeepCornerAddonFrameValidation(this));
        this.registerValidation(new RestrictLDividerChildrenValidationRule(this));
        this.registerValidation(new RestrictDoorsOnCornerBaseFrameValidationRule(this));
        this.registerValidation(new LDividerIn75cmFrameCanNotHave25cmDoorInFrontRule(this));
        this.registerValidation(new RestrictHandlesOnCornerSolution(this));
        // Register planner systems
        this.registerSystem(new AutoAdaptClothRailSystem(this));
        this.registerSystem(new AutofillCornerSystem(this));
        this.registerSystem(new AutofillSingleWallSystem(this));
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new PaxFrameGroupingSystem(this));
        this.registerSystem(new InSceneEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new DoorsNextToCornerSolutionSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new AlignmentConnectionCreationSystem(this.getServices()));
        this.registerSystem(new CornerHingeSystem(this.getServices()));
        this.registerSystem(new CornerSolutionShelfSystem(this));
        this.registerSystem(new CollisionSystem(this, []));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new ConflictSystem(this));
        this.registerSystem(new ConflictOutlineSystem(this));
        this.registerSystem(new PaxLightsAccessoriesSystem(this));
        this.registerSystem(new AutoAdaptAccessoriesSystem(this));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new AnimationSystem(this));
        this.registerSystem(new DraggableWhenSelectedSystem(this, [
            ProductType.PAX_HINGE_DOOR_ASM,
            ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
            ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
        ]));
        return this.loadProductCatalogs(config.productCatalogs);
    }
    initSceneApi() {
        this.scene = new PaxSceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
        if (spaceEnabled) {
            this.registerSystem(new PaxMeasurementsSystem(this));
        }
        else {
            this.registerSystem(new MeasurementsWidgetSystem(this));
        }
    }
    initCameraApi(canvas) {
        this.camera = new PaxCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
    }
    getMainProductCatalog() {
        return this.catalogs['pax'];
    }
}
//# sourceMappingURL=PaxCoreApi.js.map